import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * @function formatCnpjCpf
 * @description Formata o CPF ou CNPJ
 * @param {string} value - O valor a ser formatado
 * @returns {string} - O valor formatado
 */
export function formatCnpjCpf(value) {
  if (!value || typeof value !== 'string') return "";
  const cnpjCpf = value.replace(/\D/g, "");

  if (cnpjCpf.length === 11) {
    return cnpjCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "$1.$2.$3-$4");
  }

  return cnpjCpf.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g,
    "$1.$2.$3/$4-$5"
  );
}

/**
 * @function parseDate
 * @description Faz o parse de uma data de forma segura, tratando diferentes formatos e timezones
 * @param {string|Date} dateInput - Data em qualquer formato (ISO, string, Date object)
 * @returns {object|null} - Objeto dayjs válido ou null se inválido
 * @private
 */
function parseDate(dateInput) {
  if (!dateInput) return null;
  
  try {
    let date;
    
    if (typeof dateInput === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        date = dayjs(dateInput, "YYYY-MM-DD");
      }
      else if (dateInput.includes("T") || dateInput.includes("Z")) {
        const datePart = dateInput.split("T")[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          date = dayjs(datePart, "YYYY-MM-DD");
        } else {
          date = dayjs(dateInput);
        }
      }
      else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)) {
        const parts = dateInput.split("/");
        const first = parseInt(parts[0], 10);
        const second = parseInt(parts[1], 10);

        if (first > 12) {
          date = dayjs(dateInput, "DD/MM/YYYY");
        }
        else if (second > 12) {
          date = dayjs(dateInput, "MM/DD/YYYY");
        }
        else {
          date = dayjs(dateInput, "DD/MM/YYYY");
          if (!date.isValid()) {
            date = dayjs(dateInput, "MM/DD/YYYY");
          }
        }
      }
      else {
        date = dayjs(dateInput);
      }
    } 
    else if (dateInput instanceof Date) {
      date = dayjs(dateInput);
    }
    else {
      date = dayjs(dateInput);
    }

    return date.isValid() ? date : null;
  } catch (error) {
    console.warn("Erro ao parsear data:", dateInput, error);
    return null;
  }
}

/**
 * @function parseDateSafe
 * @description Faz o parse de uma data de forma segura, retornando no formato YYYY-MM-DD para uso em inputs
 * @param {string|Date} dateInput - Data em qualquer formato (ISO, string, Date object)
 * @returns {string|null} - Data formatada em YYYY-MM-DD ou null se inválida
 */
export function parseDateSafe(dateInput) {
  const date = parseDate(dateInput);
  return date ? date.format("YYYY-MM-DD") : null;
}

/**
 * @function formatDateSafe
 * @description Formata uma data de forma segura, tratando diferentes formatos e timezones
 * @param {string|Date} dateInput - Data em qualquer formato (ISO, string, Date object)
 * @returns {string} - Data formatada em DD/MM/YYYY ou "-" se inválida
 */
export function formatDateSafe(dateInput) {
  if (!dateInput || (typeof dateInput === "string" && dateInput.trim() === "")) {
    return "-";
  }
  
  const date = parseDate(dateInput);
  return date ? date.format("DD/MM/YYYY") : "-";
}

/**
 * @function formatCurrency
 * @description Formata um valor numérico para o formato de moeda brasileira (R$)
 * @param {number|string} value - Valor a ser formatado
 * @returns {string} - Valor formatado em R$ 0.000,00
 */
export function formatCurrency(value) {
  if (value === null || value === undefined || value === "") {
    return "R$ 0,00";
  }
  
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return "R$ 0,00";
  }
  
  return numValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}